import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { API, MEDIA_URL } from '../config';
import RateUsModal from './RateUsModal';
import PlanBadge from './PlanBadge';
import '../styles/theme.css';

function RecruiterSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [unread, setUnread] = useState(0);
  const [userTier, setUserTier] = useState(localStorage.getItem('user_tier'));

  useEffect(() => {
    const full = localStorage.getItem('user_name') || '';
    setDisplayName(full.split(' ')[0] || 'Recruiter');
    setUserTier(localStorage.getItem('user_tier'));

    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/recruiter-profile/me/`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            setCompanyName(data.company_name || '');
            if (data.contact_person_name) setDisplayName(data.contact_person_name);
            if (data.company_logo) {
              const logo = data.company_logo.startsWith('http')
                ? data.company_logo
                : `${MEDIA_URL}${data.company_logo.startsWith('/') ? '' : '/'}${data.company_logo}`;
              setLogoUrl(logo);
            }
          }
        })
        .catch(() => {});

      // Sync tier from backend so badge is always accurate
      fetch(`${API}/payments/my-tier/`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.tier) {
            setUserTier(d.tier);
            localStorage.setItem('user_tier', d.tier);
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchUnread = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      fetch(`${API}/messages/unread-count/`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setUnread(d.unread); }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  const initials = displayName ? displayName.slice(0, 2).toUpperCase() : 'R';
  const [showRateUs, setShowRateUs] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navItems = [
    { label: 'Dashboard', icon: 'fa-th-large', path: '/recruiter/dashboard', tour: 'dashboard' },
    { label: 'Post a Job', icon: 'fa-plus-circle', path: '/recruiter/post-job', tour: 'post-job' },
    { label: 'Find Candidates', icon: 'fa-users', path: '/recruiter/search-seekers', tour: 'find-candidates' },
    { label: 'Messages', icon: 'fa-comments', path: '/recruiter/messages', badge: unread || null },
    { label: 'Analytics', icon: 'fa-chart-line', path: '/recruiter/analytics', tour: 'analytics' },
    { label: 'Email Templates', icon: 'fa-envelope-open-text', path: '/recruiter/email-templates' },
    { label: 'Company Profile', icon: 'fa-building', path: '/recruiter/company', tour: 'company-profile' },
    { label: 'Upgrade Plan', icon: 'fa-crown', path: '/pricing', tour: 'pricing' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('company_name');
    localStorage.removeItem('user_name');
    localStorage.removeItem('userType');
    localStorage.removeItem('user_type');
    navigate('/recruiter/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
    <div
      className={`sidebar-overlay${mobileOpen ? ' visible' : ''}`}
      onClick={() => setMobileOpen(false)}
    />
    <button
      className="sidebar-hamburger"
      onClick={() => setMobileOpen(o => !o)}
      aria-label="Toggle menu"
    >
      <i className={`fas ${mobileOpen ? 'fa-times' : 'fa-bars'}`}></i>
    </button>
    <aside className={`sidebar${mobileOpen ? ' open' : ''}`}>
      <a className="sidebar-logo" onClick={() => navigate('/recruiter/dashboard')} style={{ cursor: 'pointer' }}>
        <img src="/JobLinkedAI.png" alt="JobLinked AI" className="sidebar-logo-img" />
        <span className="sidebar-logo-text">Job<span>Linked</span> AI</span>
      </a>

      <nav className="sidebar-nav">
        <span className="sidebar-section-label">Recruiter</span>
        {navItems.map(item => (
          <button
            key={item.path}
            className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
            data-tour={item.tour}
          >
            <i className={`fas ${item.icon}`}></i>
            {item.label}
            {item.badge > 0 && <span className="sidebar-badge">{item.badge}</span>}
          </button>
        ))}
      </nav>

      <div className="sidebar-footer">
        <button className="sidebar-link" onClick={() => setShowRateUs(true)} style={{ color: '#f59e0b' }}>
          <i className="fas fa-star"></i>
          Rate Us
        </button>
        <div className="sidebar-user">
          <div
            className="sidebar-avatar"
            onClick={() => navigate('/recruiter/company')}
            title="View company profile"
            style={{ cursor: 'pointer' }}
          >
            {logoUrl
              ? <img src={logoUrl} alt={companyName} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', background: 'white', padding: '2px' }} />
              : initials
            }
          </div>
          <div className="sidebar-user-info" onClick={() => navigate('/recruiter/company')} style={{ cursor: 'pointer' }}>
            <div className="sidebar-user-name">{displayName}</div>
            <div className="sidebar-user-role">{companyName}</div>
            <PlanBadge tier={userTier} size="sm" />
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.4rem',
              borderRadius: '6px',
              color: '#475569',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = 'none'; }}
          >
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    </aside>
    {showRateUs && <RateUsModal onClose={() => setShowRateUs(false)} />}
    </>
  );
}

export default RecruiterSidebar;
